import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent,
  DialogTitle, IconButton, InputAdornment, List, ListItemButton,
  ListItemText, MenuItem, Select, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import CloseIcon  from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { loadActivities, loadActivityDetail, loadPickerOptions } from '../../reducers/activityReducer'

const TECH_COLORS = {
  '5G': { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4' },
  '4G': { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
  '3G': { bg: '#fff3e0', color: '#bf360c', border: '#ffcc80' },
  '2G': { bg: '#fce4ec', color: '#880e4f', border: '#f48fb1' },
}

const STATUS_COLORS = {
  active:   { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7', label: 'Active' },
  planned:  { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9', label: 'Planned' },
  overdue:  { bg: '#fff3e0', color: '#e65100', border: '#ffcc80', label: 'Overdue' },
  closed:   { bg: '#f5f5f5', color: '#757575', border: '#e0e0e0', label: 'Closed' },
}

function getStatus(activity) {
  const status    = (activity.status ?? activity.Status ?? '').toLowerCase()
  const isActive  = activity.isActive  == 1
  const isOverdue = activity.isOverdue == 1
  const today     = new Date().toISOString().slice(0, 10)
  const startDate = activity.startDate ?? activity.StartDate ?? ''
  const endDate   = activity.endDate   ?? activity.EndDate   ?? ''

  if (isOverdue)                          return 'overdue'
  if (startDate > today)                  return 'planned'
  if (status !== 'open' && !isActive)     return 'closed'
  if (endDate && endDate < today)         return 'closed'
  return 'active'
}

export default function ActivityKPIDashboardActivityPicker({ open, onClose, onConfirm }) {
  const dispatch = useDispatch()
  const { list, listLoading, pickerOptions, pickerOptionsLoading } = useSelector(s => s.activity)

  const [search,       setSearch]       = useState('')
  const [pending,      setPending]      = useState(null)
  // Must be explicitly chosen by the user before Apply is enabled
  // Vendor comes first — privilege is vendor-scoped, and Technology cascades off it
  const [vendorFilter, setVendorFilter] = useState('')
  const [techFilter,   setTechFilter]   = useState('')
  // Status filter: 'all' | 'active' | 'closed' | 'overdue'
  const [statusFilter, setStatusFilter] = useState('active')
  const [hasApplied,   setHasApplied]   = useState(false)

  const canApply = techFilter !== '' && vendorFilter !== ''

  // Full Vendor/Technology universe (scoped by privilege), fetched once when
  // the dialog opens — not the (heavy) activities list, and not refetched on
  // every Vendor change. The cascade below is derived from this in memory.
  useEffect(() => {
    if (open) dispatch(loadPickerOptions())
  }, [open]) // eslint-disable-line

  const combos = pickerOptions.combos

  const vendors = useMemo(
    () => [...new Set(combos.map(c => c.vendor))].sort(),
    [combos]
  )

  // Technology options cascade off the selected vendor — pure in-memory filter,
  // zero API calls.
  const technologies = useMemo(() => {
    const relevant = (vendorFilter === '' || vendorFilter === 'All')
      ? combos
      : combos.filter(c => c.vendor === vendorFilter)
    return [...new Set(relevant.map(c => c.technology))].sort()
  }, [combos, vendorFilter])

  // Changing Vendor just resets Technology locally — the cascade recomputes
  // from the combo list already in memory.
  const handleVendorChange = (value) => {
    setVendorFilter(value)
    setTechFilter('')
  }

  // Single fetch, only on Apply — Tech/Vendor/Search all go through here together
  const handleApply = () => {
    if (!canApply) return
    setPending(null)
    setHasApplied(true)
    dispatch(loadActivities({
      search:     search || undefined,
      technology: techFilter === 'All' ? undefined : techFilter,
      vendor:     vendorFilter === 'All' ? undefined : vendorFilter,
    }))
  }

  // Client-side status filter
  const filtered = useMemo(() => {
    if (statusFilter === 'all') return list
    return list.filter(a => getStatus(a) === statusFilter)
  }, [list, statusFilter])

  const handleConfirm = async () => {
    if (!pending) return
    const result = await dispatch(loadActivityDetail(pending.activityId))
    const detail = result.payload
    onConfirm(detail)
    onClose()
  }

  const techChip = (tech) => {
    const c = TECH_COLORS[tech] ?? TECH_COLORS['4G']
    return (
      <Chip key={tech} label={tech} size="small"
        sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: 9, height: 18 }} />
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={600} fontSize={15}>Select Activity</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      {/* Vendor / Technology — required before Apply fetches the list.
          Vendor first: privilege is vendor-scoped, and Technology cascades off it.
          Options are real distinct DB values scoped to the user's privilege, not hardcoded. */}
      <Box sx={{ px: 3, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Select
          size="small" displayEmpty value={vendorFilter}
          onChange={e => handleVendorChange(e.target.value)}
          disabled={pickerOptionsLoading}
          sx={{ fontSize: 12, minWidth: 110 }}
        >
          <MenuItem value="" disabled><em>Vendor…</em></MenuItem>
          <MenuItem value="All">All vendors</MenuItem>
          {vendors.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
        </Select>

        <Select
          size="small" displayEmpty value={techFilter}
          onChange={e => setTechFilter(e.target.value)}
          disabled={pickerOptionsLoading || vendorFilter === ''}
          sx={{ fontSize: 12, minWidth: 110 }}
        >
          <MenuItem value="" disabled><em>Technology…</em></MenuItem>
          <MenuItem value="All">All technologies</MenuItem>
          {technologies.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </Select>

        <Button variant="contained" size="small" disabled={!canApply} onClick={handleApply} sx={{ ml: 'auto' }}>
          Apply
        </Button>
      </Box>

      {/* Search — only used once Apply has run, so it lives below the filters */}
      <Box sx={{ px: 3, pb: 1 }}>
        <TextField
          fullWidth size="small" placeholder="Search by title, team or technology…"
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleApply() }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
        />
      </Box>

      {/* Status filter */}
      <Box sx={{ px: 3, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Show:
        </Typography>
        <ToggleButtonGroup
          value={statusFilter} exclusive size="small"
          onChange={(_, v) => { if (v) setStatusFilter(v) }}
          sx={{ height: 24 }}
        >
          {[
            { value: 'all',     label: 'All' },
            { value: 'active',  label: 'Active',  ...STATUS_COLORS.active },
            { value: 'planned', label: 'Planned', ...STATUS_COLORS.planned },
            { value: 'overdue', label: 'Overdue', ...STATUS_COLORS.overdue },
            { value: 'closed',  label: 'Closed',  ...STATUS_COLORS.closed },
          ].map(s => (
            <ToggleButton key={s.value} value={s.value} sx={{
              px: 1.5, fontSize: 10, fontWeight: 600, textTransform: 'none',
              '&.Mui-selected': {
                bgcolor: s.bg ?? 'primary.light',
                color:   s.color ?? 'primary.main',
                border:  `1px solid ${s.border ?? '#90caf9'} !important`,
              },
            }}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography sx={{ fontSize: 10, color: 'text.disabled', ml: 'auto' }}>
          {filtered.length} activities
        </Typography>
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
        {listLoading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
          : !hasApplied
            ? <Typography align="center" color="text.disabled" sx={{ p: 4 }}>Choose a technology and vendor, then Apply to load activities</Typography>
            : filtered.length === 0
            ? <Typography align="center" color="text.disabled" sx={{ p: 4 }}>No activities found</Typography>
            : (
              <List dense disablePadding>
                {filtered.map(a => {
                  const status = getStatus(a)
                  const sc     = STATUS_COLORS[status]
                  return (
                    <ListItemButton
                      key={a.activityId}
                      selected={pending?.activityId === a.activityId}
                      onClick={() => setPending(a)}
                      sx={{
                        borderBottom: '1px solid', borderColor: 'divider', px: 2.5, py: 1.25,
                        '&.Mui-selected':       { bgcolor: 'primary.light' },
                        '&.Mui-selected:hover': { bgcolor: '#bbdefb' },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontSize={13} fontWeight={500}>{a.title}</Typography>
                            <Chip label={sc.label} size="small"
                              sx={{ bgcolor: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 700, fontSize: 9, height: 16 }} />
                          </Box>
                        }
                        secondary={
                          <Typography fontSize={11} color="text.disabled" sx={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                            {a.siteCount} sites · {a.startDate ?? 'today'} → {a.endDate ?? 'today'} · {a.team}
                          </Typography>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1.5, flexShrink: 0 }}>
                        {a.technology?.split(',').map(t => techChip(t.trim()))}
                        <Chip label={a.vendor} size="small"
                          sx={{ bgcolor: '#f5f5f5', color: '#555', border: '1px solid #ddd', fontSize: 9, height: 18, fontWeight: 600 }} />
                      </Box>
                    </ListItemButton>
                  )
                })}
              </List>
            )
        }
      </DialogContent>

      <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" size="small" onClick={onClose}>Cancel</Button>
        <Button variant="contained" size="small" disabled={!pending} onClick={handleConfirm}>
          Confirm selection
        </Button>
      </Box>
    </Dialog>
  )
}